// Mock data for Quality Power Electrical Equipments Limited
// Content sourced/paraphrased from qualitypower.com — restyled into dark deep-tech theme.

export const companyInfo = {
  name: "Quality Power",
  legalName: "Quality Power Electrical Equipments Limited",
  tagline: "Powering What's Next",
  description:
    "Quality Power is an Indian player serving global clients in critical energy transition equipment and power technologies. We provide high voltage electrical equipment and solutions for electrical grid connectivity and energy transition — spanning power generation, transmission, distribution, and automation, plus emerging applications like large-scale renewables.",
  visionTitle: "Our Vision for the Future",
  vision:
    "Our vision is to lead innovative power solutions and make clean, reliable energy accessible to all. We are committed to creating a sustainable future and making power a fundamental human right. Together, we can build a limitless and innovative future powered by sustainability.",
  aboutLong: [
    "We are among the few global manufacturers of critical high voltage equipment for High Voltage Direct Current (HVDC) and Flexible AC Transmission Systems (FACTS) networks. These equipment and networks form key components for energy transition from renewable sources to traditional power grids.",
    "With over two decades of experience in the energy transition space, we provide an extensive range of products crucial for effective power transmission and advanced power automation — including reactors, transformers, line traps, instrument transformers, capacitor banks, converters, harmonic filters, and reactive power compensation systems.",
    "Our grid interconnection solutions feature technologies such as STATCOM and Static VAR Compensator (SVC) systems. Our domestic and global footprint allows us to cater to both Indian and global customer bases, including several Fortune 500 conglomerates.",
    "Our manufacturing operations in India are spread across two locations — Sangli, Maharashtra, and Aluva, Kerala. As part of our global expansion, we acquired 51% of the share capital in Endoks Enerji Dağıtım Sistemleri Sanayi İthalat ve İhracat Limited Şirketi in 2011, which has design, operation, assembly, project management and delivery facilities in Ankara, Turkey."
  ]
};

export const heroStats = [
  { value: "120+", suffix: "", label: "Countries Served", icon: "Globe2" },
  { value: "765", suffix: "kV", label: "Systems Tested — NABL Lab, Sangli", icon: "Gauge" },
  { value: "51", suffix: "%", label: "Stake in Endoks Enerji, Turkey", icon: "Network" },
  { value: "2", suffix: " Decades", label: "Of Energy Transition Expertise", icon: "Award" }
];

export const applications = [
  { title: "Electric Utilities", icon: "Zap" },
  { title: "Power Plants", icon: "Factory" },
  { title: "Industries", icon: "Building2" },
  { title: "Smart Grids", icon: "Network" },
  { title: "Renewable Energy", icon: "Leaf" },
  { title: "Distribution Systems", icon: "Share2" },
  { title: "Mobility", icon: "Car" },
  { title: "FACTS & HVDC", icon: "Waves" }
];

export const productPortfolio = [
  {
    slug: "power-products",
    title: "Power Products",
    icon: "Boxes",
    description:
      "Reactors, transformers, PLCC components, instrument transformers and composite technologies engineered for high voltage transmission networks.",
    stats: [{ value: "5", label: "Categories" }, { value: "765kV", label: "Max Rating Tested" }],
    items: ["Reactors", "Transformers", "PLCC Components", "Instrument Transformers", "Edison Composites"]
  },
  {
    slug: "power-quality",
    title: "Power Quality",
    icon: "Activity",
    description:
      "Capacitor banks, harmonic filters, SVC & STATCOM systems that stabilise grids and enable smooth integration of renewable energy sources.",
    stats: [{ value: "6", label: "Solution Lines" }, { value: "HVDC", label: "& FACTS Ready" }],
    items: ["Capacitor Banks", "Harmonic Filters", "Static Var Compensators", "STATCOM", "Metal Enclosed Capacitor Banks", "Reactive Power Compensation"]
  },
  {
    slug: "corporate",
    title: "Corporate",
    icon: "Building",
    description:
      "An Indian, BSE & NSE listed energy transition company with two decades of manufacturing excellence and global governance standards.",
    stats: [{ value: "2001", label: "Incorporated" }, { value: "BSE/NSE", label: "Listed" }],
    items: ["About Us", "Management", "Awards & Accolades", "Milestones"]
  }
];

export const powerProductsCategories = [
  {
    slug: "reactors",
    title: "Reactors",
    icon: "CircuitBoard",
    summary: "Series, Shunt, FACTS/HVDC and Iron Core reactors engineered for grid stability and power quality.",
    description:
      "Our reactors form a critical part of high voltage transmission and FACTS/HVDC networks, providing reactive power control, fault current limitation and harmonic mitigation across utility and industrial grids.",
    items: [
      { name: "Series Reactors", detail: "Limit fault currents and balance load sharing across parallel transmission lines." },
      { name: "Shunt Reactors", detail: "Compensate capacitive charging current on long EHV transmission lines." },
      { name: "FACTS/HVDC Reactors", detail: "Purpose built for FACTS and HVDC converter station applications." },
      { name: "Iron Core Reactors", detail: "Compact iron-core design for current limiting and filtering duties." }
    ]
  },
  {
    slug: "transformers",
    title: "Transformers",
    icon: "Boxes",
    summary: "Special purpose, earthing, converter duty, dry type and arc furnace transformers.",
    description:
      "A wide transformer portfolio engineered for demanding industrial and utility duty cycles — from rectifier and converter applications to dry-type installations requiring enhanced safety.",
    items: [
      { name: "Special Purpose Rectifier Transformers", detail: "Engineered for rectifier and DC power supply applications." },
      { name: "Earthing Transformers", detail: "Provide neutral grounding for ungrounded power systems." },
      { name: "Converter Duty Transformers", detail: "Built for harmonic-rich converter and drive applications." },
      { name: "Dry Type Transformers", detail: "Cast-resin construction for indoor and fire-sensitive installations." },
      { name: "Arc Furnace Transformers", detail: "Heavy duty transformers for electric arc furnace operations." }
    ]
  },
  {
    slug: "plcc-components",
    title: "PLCC Components",
    icon: "Radio",
    summary: "Wave traps and line matching units for power line carrier communication.",
    description:
      "Power Line Carrier Communication (PLCC) components enable secure voice, data and protection signalling over high voltage transmission lines.",
    items: [
      { name: "Wave Traps", detail: "Line traps that isolate carrier frequency signals from the power network." },
      { name: "LMU / LMDU", detail: "Line Matching Units / Line Matching & Drainage Units for PLCC coupling." }
    ]
  },
  {
    slug: "instrument-transformers",
    title: "Instrument Transformers",
    icon: "Gauge",
    summary: "Current, potential and discharge coil transformers for metering & protection.",
    description:
      "Precision instrument transformers deliver accurate metering and protection signals across substations and switchyards up to extra high voltage levels.",
    items: [
      { name: "Current Transformers", detail: "Step down high line currents for metering & protection relays." },
      { name: "Potential Transformers", detail: "Step down high voltages for accurate metering and protection." },
      { name: "Discharge Coil / PT's Transformers", detail: "Discharge residual charge to protect capacitor bank installations." }
    ]
  },
  {
    slug: "edison-composites",
    title: "Edison Composites",
    icon: "Cpu",
    summary: "Advanced composite insulation technologies for high voltage applications.",
    description:
      "Edison Composites brings advanced composite material technology to high voltage insulation applications, improving durability and performance across our product range.",
    items: [
      { name: "Composite Insulation Systems", detail: "Engineered composite materials for enhanced high voltage insulation performance." }
    ]
  }
];

// Optional per category: `image` (a path under frontend/public, e.g.
// "/images/power-quality/capacitor-banks.jpg") and `story` (array of paragraphs,
// shown on the detail page in place of `description`).
export const powerQualityCategories = [
  {
    slug: "capacitor-banks",
    image: "/images/power-quality/QP_Header-Images_Capacitors-bank.jpg",
    title: "Capacitor Banks",
    icon: "Boxes",
    summary: "Reactive power compensation to improve power factor and reduce network losses.",
    description:
      "Our capacitor banks improve power factor, reduce transmission losses and stabilise voltage across industrial, utility and distribution networks."
  },
  {
    slug: "harmonic-filters",
    image: "/images/power-quality/QP_Header-Images_Harmonic-Filters.jpg",
    title: "Harmonic Filters",
    icon: "Filter",
    summary: "Mitigate harmonic distortion and protect sensitive grid-connected equipment.",
    description:
      "Engineered filter solutions mitigate harmonic distortion introduced by non-linear loads, protecting equipment and maintaining power quality compliance."
  },
  {
    slug: "static-var-compensators",
    image: "/images/power-quality/QP_Header-Images_Static-Var.jpg",
    title: "Static Var Compensators",
    icon: "Waves",
    summary: "Dynamic voltage regulation and reactive power compensation (SVC).",
    description:
      "SVC systems provide dynamic voltage regulation and reactive power compensation, critical for stable integration of variable renewable generation."
  },
  {
    slug: "statcom",
    image: "/images/power-quality/QP_Header-Images_Statcom.jpg",
    title: "STATCOM",
    icon: "Zap",
    summary: "Static Synchronous Compensators for grid stability and reliability.",
    description:
      "Static Synchronous Compensators (STATCOM) manage fluctuations from variable renewable energy sources through fast, dynamic reactive power support."
  },
  {
    slug: "metal-enclosed-capacitor-banks",
    image: "/images/power-quality/QP_Header-Images_Metal-Enclosed.jpg",
    title: "Metal Enclosed Capacitor Banks",
    icon: "ShieldCheck",
    summary: "Compact, weather-protected enclosures for utility & industrial substations.",
    description:
      "Compact, safe and weather-protected capacitor bank enclosures engineered for utility and industrial substation deployment."
  },
  {
    slug: "reactive-power-compensation",
    image: "/images/power-quality/reactive-power-compensation.svg",
    title: "Reactive Power Compensation Systems",
    icon: "Activity",
    summary: "Integrated systems that optimise reactive power flow across the network.",
    description:
      "Integrated reactive power compensation systems optimise power flow across the grid, enhancing network performance and operational reliability."
  }
];

export const managementTeam = [
  {
    name: "Mr. P. T. Pandyan",
    initials: "PT",
    title: "Chairman and Managing Director",
    bio: "Thalavaidurai Pandyan is the Chairman and Managing Director and one of the Promoters of the Company, associated with it since incorporation. He holds a diploma in electrical engineering (industrial electronics) and brings 39 years of overall work experience, having previously served as chief design engineer at Hind Rectifiers Limited and Works Manager at PS Electricals (P) Limited."
  },
  {
    name: "Mr. Bharanidharan Pandyan",
    initials: "BP",
    title: "Joint Managing Director",
    bio: "Bharanidharan Pandyan is the Joint Managing and Whole-time Director and one of the Promoters of the Company, associated with it since July 2002, with 22 years of overall experience. He holds a postgraduate programme in management specialising in family managed business from S.P. Jain Institute of Management and Research, Mumbai."
  },
  {
    name: "Mrs. Chitra Pandyan",
    initials: "CP",
    title: "Director",
    bio: "Chitra Pandyan is a Whole-time Director and one of the Promoters of the Company, associated with it since incorporation with 22 years of overall experience in the Company."
  },
  {
    name: "Mr. Mahesh Saralaya",
    initials: "MS",
    title: "Director",
    bio: "Mahesh Vitthal Saralaya is a Whole-time Director, associated with the Company since October 2006. He holds a bachelor's degree in electrical engineering from Manipal Institute of Technology and is a chartered engineer registered with the Institution of Engineers (India), with 18 years of overall experience."
  },
  {
    name: "Mr. Shailesh Kumar Mishra",
    initials: "SM",
    title: "Independent Director",
    bio: "Shailesh Kumar Mishra holds a bachelor's degree in electrical engineering from NIT Bhopal. He was previously an Executive Director at Power Grid Corporation of India, with 38 years of overall experience."
  },
  {
    name: "Ms. Pournima Kulkarni",
    initials: "PK",
    title: "Independent Director",
    bio: "Pournima Suresh Kulkarni is a practicing chartered accountant registered with ICAI, holding a bachelor's degree in commerce from Shivaji University, with 17 years of overall experience across cooperative banking boards."
  },
  {
    name: "Mr. Rajendra Iyer",
    initials: "RI",
    title: "Independent Director",
    bio: "Rajendra Sheshadri Iyer holds a bachelor's degree in engineering (industrial electronics) from University of Pune. He has held senior roles at ABB Management Services, founded Grid Solutions SAS, and worked at GE Corporate UK."
  },
  {
    name: "Mr. Sadayandi Ramesh",
    initials: "SR",
    title: "Independent Director",
    bio: "Sadayandi Ramesh is an Independent Director of the Company, bringing valuable governance experience to the Board."
  }
];

export const awards = [
  { title: "ISO 9001:2015", subtitle: "Quality Management System", icon: "BadgeCheck" },
  { title: "ISO 14001:2015", subtitle: "Environmental Management System", icon: "Leaf" },
  { title: "ISO 45001:2018", subtitle: "Occupational Health & Safety", icon: "ShieldCheck" },
  { title: "NABL Accredited", subtitle: "ISO/IEC 17025:2017 — Sangli Test & Research Lab, up to 765kV", icon: "Gauge" },
  { title: "One Star Export House", subtitle: "DGFT, Foreign Trade Policy 2023", icon: "Award" },
  { title: "BSE & NSE Listed", subtitle: "Publicly traded on India's leading exchanges", icon: "TrendingUp" }
];

export const milestones = [
  { year: "2001", title: "Company Incorporated", detail: "Quality Power Electrical Equipments Private Limited incorporated in Maharashtra, India." },
  { year: "2011", title: "Endoks Acquisition", detail: "Acquired 51% stake in Endoks Enerji, Ankara, Turkey, establishing an international manufacturing & delivery base." },
  { year: "2019", title: "S&S Transformers Acquisition", detail: "Acquired S&S Transformers & Accessories Private Limited, expanding transformer manufacturing capability." },
  { year: "2022", title: "Bangalore & Nebeskie Labs", detail: "Took over the business of Electrical Power Equipment Company, Bangalore and invested in Nebeskie Labs Private Limited." },
  { year: "2023", title: "One Star Export House", detail: "Awarded 'One Star Export House' status by the DGFT under the Foreign Trade Policy, 2023." },
  { year: "2024", title: "Converted to Public Limited Company", detail: "Company converted into a public limited company ahead of its public listing." },
  { year: "2025", title: "IPO & Mehru Acquisition", detail: "Successful IPO with listing on BSE & NSE, followed by acquisition of a majority stake in Mehru Electrical & Mechanical Engineers." }
];

// Group footprint - group companies from the Annual Report 2025-26 Board's Report
// (section 9, as on 31 March 2026) plus sales & representative offices. `map`
// picks which view the pin lives on: the India inset or the world map.
export const footprintSites = [
  {
    id: "sangli", city: "Sangli", region: "Maharashtra, India", lat: 16.85, lon: 74.58, map: "india", kind: "hq",
    companies: [
      { name: "Quality Power Electrical Equipments Ltd", role: "Parent company · Headquarters", since: "2001", nabl: true,
        focus: "HVDC & FACTS equipment - reactors, transformers, line traps, instrument transformers and power quality systems. NABL-accredited Test & Research Lab for systems up to 765kV, and the new Global Coil Manufacturing Facility." },
      { name: "Quality Power Engineering Projects Pvt Ltd", role: "Subsidiary · 98%", since: "2009",
        focus: "Design, engineering, procurement, installation and commissioning of power and energy projects." }
    ]
  },
  {
    id: "aluva", city: "Aluva (Cochin)", region: "Kerala, India", lat: 10.11, lon: 76.35, map: "india", kind: "company",
    companies: [
      { name: "S&S Transformers & Accessories Pvt Ltd", role: "Wholly-owned subsidiary · 100%", since: "2019",
        focus: "Cast resin transformers and unitized substations." }
    ]
  },
  {
    id: "bhiwadi", city: "Bhiwadi", region: "Rajasthan, India", lat: 28.21, lon: 76.86, map: "india", kind: "company", nudge: [-6, 6], labelSide: "left",
    companies: [
      { name: "Mehru Electrical & Mechanical Engineers Pvt Ltd", role: "Material subsidiary · 51%", since: "2025", nabl: true,
        focus: "High-voltage instrument transformers, with GIS and high-voltage testing infrastructure." }
    ]
  },
  {
    id: "pune", city: "Pune", region: "Maharashtra, India", lat: 18.52, lon: 73.86, map: "india", kind: "company", labelSide: "left",
    companies: [
      { name: "Sukrut Electric Company Pvt Ltd", role: "Joint venture · 50:50 with Yash Highvoltage", since: "2026", nabl: true,
        focus: "Power accessories, panels, fabrication and transformer components." }
    ]
  },
  {
    id: "chennai", city: "Chennai", region: "Tamil Nadu, India", lat: 13.08, lon: 80.27, map: "india", kind: "company",
    companies: [
      { name: "Nebeskie Labs Pvt Ltd", role: "Associate · 26%", since: "2025",
        focus: "Industrial IoT, edge computing and energy management systems." }
    ]
  },
  {
    id: "turkiye", city: "Ankara & Niğde", region: "Türkiye", lat: 39.93, lon: 32.86, map: "world", kind: "company",
    companies: [
      { name: "Endoks Enerji A.Ş.", role: "Step-down subsidiary · 51%", since: "2011", nabl: true,
        focus: "Power electronics and power quality - design, assembly, project management and delivery, plus a new Power Conversion System (BESS) facility at Niğde." }
    ]
  },
  {
    id: "vizag", city: "Visakhapatnam", region: "Andhra Pradesh, India", lat: 17.69, lon: 83.22, map: "india", kind: "company",
    companies: [
      { name: "Winwin Speciality Insulators", role: "Proposed acquisition",
        focus: "Ceramic and polymeric insulators, extending the group across the power transmission value chain." }
    ]
  },
  { id: "delhi", city: "Delhi", region: "India", lat: 28.61, lon: 77.21, map: "india", kind: "office", nudge: [6, -6] },
  { id: "kolkata", city: "Kolkata", region: "India", lat: 22.57, lon: 88.36, map: "india", kind: "office" },
  { id: "bengaluru", city: "Bengaluru", region: "India", lat: 12.97, lon: 77.59, map: "india", kind: "office", labelSide: "left" },
  { id: "dubai", city: "Dubai", region: "UAE", lat: 25.2, lon: 55.27, map: "world", kind: "office", labelSide: "left" },
  { id: "london", city: "London", region: "United Kingdom", lat: 51.51, lon: -0.13, map: "world", kind: "office", labelSide: "left" },
  { id: "toronto", city: "Toronto", region: "Canada", lat: 43.65, lon: -79.38, map: "world", kind: "office" },
  { id: "chicago", city: "Chicago", region: "USA", lat: 41.88, lon: -87.63, map: "world", kind: "office", labelSide: "left" },
  { id: "sydney", city: "Sydney", region: "Australia", lat: -33.87, lon: 151.21, map: "world", kind: "office" }
];

// Export markets named in the Annual Report without a group office of their own -
// drawn as faint destinations of the export routes on the world map.
export const exportRegions = [
  { label: "Scandinavia", lat: 60.5, lon: 13 },
  { label: "SE Asia", lat: 3, lon: 104 }
];

// One card per group company (flattened from footprintSites) - used by the
// Footprint page and the Home page teaser.
export const footprintLocations = footprintSites.flatMap((site) =>
  (site.companies || []).map((c) => ({
    title: c.name,
    type: `${site.city}, ${site.region} · ${c.role}`,
    detail: c.focus
  }))
);

export const footprintStats = [
  { prefix: "₹", value: "10070", suffix: " Mn", label: "Revenue FY 2025-26" },
  { prefix: "₹", value: "14600", suffix: " Mn", label: "Order Book" },
  { prefix: "₹", value: "1855", suffix: " Mn", label: "Profit After Tax" },
  { prefix: "~", value: "1500", suffix: "", label: "Total Workforce" },
  { value: "7", suffix: "", label: "Operating Facilities, India & Türkiye" },
  { value: "120", suffix: "+", label: "Countries Served" },
  { value: "210", suffix: "+", label: "Customers Worldwide" },
  { value: "765", suffix: "kV", label: "NABL-Accredited Testing" }
];

export const newsFeature = {
  tag: "As seen on CNBC-TV18",
  headline: "Order Win",
  company: "QUALITY POWER",
  update: "Arm Endoks Enerji Anonim Şirketi (Quality Power's Turkish subsidiary) secures a ₹152 crore BESS (Battery Energy Storage System) order.",
  segmentTitle: "Impact of War on Business from West Asia",
  spokesperson: "Bharanidharan Pandyan",
  spokespersonTitle: "Joint Managing Director",
  videoUrl: "https://www.cnbctv18.com/videos/business/companies/quality-power-bess-order-hvdc-storage-global-business-19877832.htm"
};

export const investorSections = [
  { title: "Corporate Governance", icon: "Landmark", detail: "Board composition, committees, policies and governance framework upholding transparency and accountability." },
  { title: "Investor Relations", icon: "Users", detail: "Annual reports, shareholder information and communications for the investor community." },
  { title: "Stock Exchange Disclosures", icon: "FileText", detail: "Regulatory filings and disclosures made to BSE and NSE in compliance with SEBI requirements." },
  { title: "Quarterly Earnings", icon: "CalendarClock", detail: "Quarterly financial results, investor presentations and earnings call transcripts." }
];

export const contactInfo = {
  officeName: "Quality Power Electrical Equipments Ltd.",
  addressLine: "L-61 MIDC Kupwad, Sangli, Maharashtra 416 436, India",
  phone: "+91 233 2645432",
  email: "corporate@qualitypower.co.in",
  hours: "Mon–Sat, 9:30 to 18:00"
};

export const navigation = [
  {
    label: "Corporate",
    items: [
      { label: "About Us", to: "/about-us" },
      { label: "Management", to: "/management" },
      { label: "Awards and Accolades", to: "/awards-and-accolades" },
      { label: "Milestones", to: "/milestones" }
    ]
  },
  {
    label: "Power Products",
    to: "/power-products",
    items: powerProductsCategories.map((c) => ({ label: c.title, to: `/power-products/${c.slug}` }))
  },
  {
    label: "Power Quality",
    to: "/power-quality",
    items: powerQualityCategories.map((c) => ({ label: c.title, to: `/power-quality/${c.slug}` }))
  },
  { label: "Footprint", to: "/footprint" },
  { label: "Sustainability", to: "/sustainability" },
  {
    label: "Media",
    to: "/media",
    items: [
      { label: "News", to: "/media?tab=news" },
      { label: "Corporate News", to: "/media?tab=corporate" }
    ]
  },
  {
    label: "Investors",
    to: "/investors",
    items: [
      { label: "Corporate Governance", to: "/investors#governance" },
      { label: "Investor Relations", to: "/investors#relations" },
      { label: "Stock Exchange Disclosures", to: "/investors#disclosures" },
      { label: "Quarterly Earnings", to: "/investors#earnings" }
    ]
  },
  { label: "Contact", to: "/contact" }
];
